import React from "react";
import { motion } from "framer-motion";
import {
  CreditCard,
  MessageSquare,
  CheckCircle,
  DollarSign,
  Smartphone,
  Send,
  ChevronLeft,
  Phone,
  Video,
  Zap,
  Shield,
  Clock,
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "../../ui/avatar";

const TextToPaySlide = () => {
  return (
    <div className="h-full w-full bg-slate-50 flex items-center justify-center p-6 lg:p-12 relative overflow-hidden font-sans">
      {/* Background Elements */}
      <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-emerald-100/40 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-[800px] h-[800px] bg-blue-100/40 rounded-full blur-[120px] translate-y-1/2 -translate-x-1/2" />

      <div className="flex flex-row items-center gap-8 lg:gap-16 max-w-6xl mx-auto z-10 w-full">
        {/* Left Content */}
        <div className="flex-1 space-y-8 lg:space-y-10 min-w-0">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-50 text-emerald-700 text-sm font-bold border border-emerald-200 shadow-sm">
            <DollarSign className="w-4 h-4" />
            <span>Text-to-Pay</span>
          </div>

          <div className="space-y-4">
            <h2 className="text-3xl lg:text-5xl xl:text-5xl font-bold text-slate-900 leading-[1.1] tracking-tight">
              Collect Payments{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-teal-600">
                Via Text Message.
              </span>
            </h2>
            <p className="text-lg lg:text-xl text-slate-500 leading-relaxed max-w-xl font-medium">
              Send secure payment links directly to patients' phones. They tap,
              pay, and you get paid — all without a phone call or front desk
              interaction.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
              <div className="text-2xl font-bold text-emerald-600 mb-1">
                3x
              </div>
              <div className="text-xs text-slate-500 uppercase font-bold tracking-wide">
                Faster Collections
              </div>
            </div>
            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
              <div className="text-2xl font-bold text-emerald-600 mb-1">
                &lt;60s
              </div>
              <div className="text-xs text-slate-500 uppercase font-bold tracking-wide">
                Avg Payment Time
              </div>
            </div>
          </div>

          <div className="space-y-3 lg:space-y-4">
            {[
              {
                icon: Zap,
                color: "emerald",
                title: "Instant Secure Links",
                desc: "HIPAA-compliant payment links sent via SMS in one click.",
              },
              {
                icon: Clock,
                color: "blue",
                title: "Automated Reminders",
                desc: "Auto-send payment reminders for outstanding balances.",
              },
              {
                icon: Shield,
                color: "indigo",
                title: "PCI Compliant",
                desc: "Bank-level encryption. No card data stored on your end.",
              },
            ].map((item, i) => (
              <div
                key={i}
                className="flex items-center gap-4 lg:gap-5 p-3 lg:p-4 rounded-2xl bg-white border border-slate-100 shadow-sm hover:shadow-md transition-shadow"
              >
                <div
                  className={`w-10 h-10 lg:w-12 lg:h-12 rounded-xl bg-${item.color}-50 flex items-center justify-center text-${item.color}-600 shrink-0`}
                >
                  <item.icon className="w-5 h-5 lg:w-6 lg:h-6" />
                </div>
                <div className="min-w-0">
                  <h4 className="font-bold text-slate-900 text-base lg:text-lg truncate">
                    {item.title}
                  </h4>
                  <p className="text-slate-500 text-sm font-medium truncate">
                    {item.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right Visual - Phone showing Text-to-Pay conversation */}
        <motion.div
          initial={{ y: 40, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="flex-1 w-full max-w-[340px] lg:max-w-[380px] xl:max-w-[420px] shrink-0"
        >
          <div className="relative aspect-[9/18] bg-slate-900 rounded-[3rem] shadow-2xl p-3 ring-8 ring-slate-800 border-4 border-slate-700">
            {/* Dynamic Island / Notch */}
            <div className="absolute top-6 left-1/2 -translate-x-1/2 w-28 h-7 bg-black rounded-full z-20 flex items-center justify-center gap-2">
              <div className="w-2 h-2 rounded-full bg-slate-800" />
              <div className="w-1.5 h-1.5 rounded-full bg-slate-800" />
            </div>

            {/* Screen Content */}
            <div className="h-full bg-white rounded-[2.5rem] overflow-hidden flex flex-col relative">
              {/* Status Bar */}
              <div className="h-14 bg-white/80 backdrop-blur-md sticky top-0 z-10 flex items-center justify-between px-6 pt-2">
                <span className="text-xs font-semibold text-slate-900 ml-2">
                  9:41
                </span>
                <div className="flex gap-1.5 mr-2">
                  <div className="w-4 h-2.5 bg-slate-900 rounded-[2px]" />
                  <div className="w-0.5 h-1 bg-slate-900/30" />
                </div>
              </div>

              {/* App Header */}
              <div className="px-4 pb-4 border-b border-slate-100 flex items-center justify-between bg-white/80 backdrop-blur-md sticky top-14 z-10">
                <div className="flex items-center gap-1 text-blue-600">
                  <ChevronLeft className="w-6 h-6" />
                  <span className="text-lg">Back</span>
                </div>
                <div className="flex flex-col items-center">
                  <Avatar className="w-10 h-10 mb-1 border border-slate-100">
                    <AvatarImage src="/assets/logo-icon.png" />
                    <AvatarFallback className="bg-emerald-600 text-white text-xs font-bold">
                      AD
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-xs font-bold text-slate-900">
                    Asha Dental
                  </span>
                </div>
                <div className="flex gap-4 text-blue-600">
                  <Video className="w-6 h-6 opacity-20" />
                  <Phone className="w-6 h-6 opacity-20" />
                </div>
              </div>

              {/* Messages Area */}
              <div className="flex-1 p-4 space-y-5 overflow-y-auto bg-slate-50">
                <div className="text-center">
                  <span className="text-[10px] text-slate-400 font-medium uppercase tracking-wider">
                    Today 2:15 PM
                  </span>
                </div>

                {/* Outgoing - Payment notification */}
                <motion.div
                  initial={{ opacity: 0, y: 10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ delay: 0.4 }}
                  className="flex flex-col items-start max-w-[88%]"
                >
                  <div className="bg-slate-200 text-slate-800 px-4 py-3 rounded-2xl rounded-tl-sm text-[15px] leading-relaxed shadow-sm">
                    Hi Sarah! You have an outstanding balance of{" "}
                    <span className="font-bold">$145.00</span> for your visit on
                    Jan 15. Pay securely here:
                  </div>
                </motion.div>

                {/* Payment Link Card */}
                <motion.div
                  initial={{ opacity: 0, y: 10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ delay: 1.0 }}
                  className="flex flex-col items-start max-w-[88%]"
                >
                  <div className="bg-white rounded-2xl rounded-tl-sm shadow-md border border-slate-200 overflow-hidden w-full">
                    <div className="bg-gradient-to-r from-emerald-500 to-teal-500 p-4 flex items-center gap-3">
                      <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                        <CreditCard className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="text-white font-bold text-sm">
                          Secure Payment
                        </p>
                        <p className="text-emerald-100 text-xs">
                          pay.ashahealth.com
                        </p>
                      </div>
                    </div>
                    <div className="p-4 space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-slate-500 text-sm">
                          Amount Due
                        </span>
                        <span className="text-slate-900 font-bold text-lg">
                          $145.00
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-slate-500 text-xs">
                          Visit: Jan 15, 2026
                        </span>
                        <span className="text-emerald-600 text-xs font-semibold">
                          Tap to Pay
                        </span>
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Patient reply */}
                <motion.div
                  initial={{ opacity: 0, y: 10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ delay: 2.0 }}
                  className="flex flex-col items-end self-end max-w-[85%] ml-auto"
                >
                  <div className="bg-blue-600 text-white px-4 py-3 rounded-2xl rounded-tr-sm text-[15px] leading-relaxed shadow-md">
                    Done! Just paid. So easy 🙌
                  </div>
                  <span className="text-[10px] text-slate-400 mt-1 font-medium">
                    Read 2:18 PM
                  </span>
                </motion.div>

                {/* Confirmation */}
                <motion.div
                  initial={{ opacity: 0, y: 10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ delay: 3.0 }}
                  className="flex flex-col items-start max-w-[88%]"
                >
                  <div className="bg-slate-200 text-slate-800 px-4 py-3 rounded-2xl rounded-tl-sm text-[15px] leading-relaxed shadow-sm">
                    Payment of $145.00 received! ✅ Your balance is now{" "}
                    <span className="font-bold text-emerald-700">$0.00</span>.
                    Thank you, Sarah!
                  </div>
                </motion.div>
              </div>

              {/* Input Area */}
              <div className="p-3 bg-white border-t border-slate-100 flex items-center gap-3 pb-8">
                <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-400">
                  <span className="text-lg leading-none mb-1">+</span>
                </div>
                <div className="flex-1 h-9 bg-slate-100 rounded-full border border-slate-200 px-4 flex items-center text-sm text-slate-400">
                  iMessage
                </div>
                <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white shadow-sm">
                  <Send className="w-4 h-4 ml-0.5" />
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default TextToPaySlide;
